
import type { MergeStep } from "./ooxml-merge";
import type { AttemptedOoxmlPair, RecoveryDrift } from "./redline-failure";
import type { RedlineFailureAttempt } from "./redline-failure-attempt";
import type {
    RedlineAgentRole,
    RedlineApplyInitiatedFrom,
    RedlineApplyOutcomeBatch,
    RedlineApplyDropCount,
    RedlineApplyOutcomeCount,
} from "./redline-apply-outcome";

interface WordErrorWire {
    code: string | null;
    message: string | null;
    location: string | null;
}

interface AttemptedOoxmlWire {
    attempted_ooxml: string;
    current_range_ooxml: string;
}

interface RecoveryDriftWire {
    reason: string;
    distance: number | null;
    expected_text: string;
    actual_text: string;
    divergence_offset: number | null;
}

interface RedlineFailureAttemptWireCommon {
    captured_at: string;
    addin_version: string;
    error_message: string;
    review_id: string | null;
}

export type RedlineFailureAttemptWire = RedlineFailureAttemptWireCommon & (
    | {
        stage: "range_unresolved";
        bookmarks: string[];
        attempted_ooxml: string;
    }
    | {
        stage: "bookmark_range_null";
        bookmarks: string[];
        attempted_ooxml: string;
    }
    | {
        stage: "invalid_bookmark_name";
        bookmarks: string[];
        invalid_bookmarks: string[];
        attempted_ooxml: string;
    }
    | {
        stage: "untracked_write_detected";
        bookmarks: string[];
        ooxml: AttemptedOoxmlWire;
        lost_paragraph_count: number;
        added_paragraph_count: number;
    }
    | {
        stage: "merge_failed";
        bookmarks: string[];
        ooxml: AttemptedOoxmlWire;
        word_error: WordErrorWire | null;
        merge_step: MergeStep;
    }
    | {
        stage: "backend_repair_exhausted";
        bookmarks: string[];
        ooxml: AttemptedOoxmlWire;
        word_error: WordErrorWire | null;
        repair_attempts: number;
        repair_failure_message: string;
    }
    | {
        stage: "agent_recovery_threw";
        bookmarks: string[];
        ooxml: AttemptedOoxmlWire;
        word_error: WordErrorWire | null;
        agent_failure_message: string;
    }
    | {
        stage: "agent_recovery_drifted";
        bookmarks: string[];
        ooxml: AttemptedOoxmlWire;
        word_error: WordErrorWire | null;
        drift: RecoveryDriftWire;
    }
    | {
        stage: "execute_js_failed";
        script: string;
        stdout: string;
        script_error: string;
    }
);

export interface RedlineFailureSnapshotRequest {
    snapshot_id: string;
    document_base64: string | null;
    attempt: RedlineFailureAttemptWire;
}

export interface RedlineApplyOutcomeBatchWire {
    apply_batch_id: string;
    apply_initiated_from: RedlineApplyInitiatedFrom;
    review_id: string | null;
    agent_role: RedlineAgentRole | null;
    entries: RedlineApplyOutcomeCount[];
    drops: RedlineApplyDropCount[];
    snapshot_ids: string[];
}

function encodeOoxmlForWire(ooxml: AttemptedOoxmlPair): AttemptedOoxmlWire {
    return {
        attempted_ooxml: ooxml.attemptedOoxml,
        current_range_ooxml: ooxml.currentRangeOoxml,
    };
}

function encodeDriftForWire(drift: RecoveryDrift): RecoveryDriftWire {
    return {
        reason: drift.reason,
        distance: drift.distance < 0 ? null : drift.distance,
        expected_text: drift.expectedText,
        actual_text: drift.actualText,
        divergence_offset: drift.divergenceOffset,
    };
}

export function encodeFailureAttemptForWire(attempt: RedlineFailureAttempt): RedlineFailureAttemptWire {
    const common: RedlineFailureAttemptWireCommon = {
        captured_at: attempt.capturedAt,
        addin_version: attempt.addinVersion,
        error_message: attempt.errorMessage,
        review_id: attempt.reviewId,
    };
    switch (attempt.stage) {
        case "range_unresolved":
            return {
                ...common,
                stage: "range_unresolved",
                bookmarks: attempt.bookmarks,
                attempted_ooxml: attempt.attemptedOoxml,
            };
        case "bookmark_range_null":
            return {
                ...common,
                stage: "bookmark_range_null",
                bookmarks: attempt.bookmarks,
                attempted_ooxml: attempt.attemptedOoxml,
            };
        case "invalid_bookmark_name":
            return {
                ...common,
                stage: "invalid_bookmark_name",
                bookmarks: attempt.bookmarks,
                invalid_bookmarks: attempt.invalidBookmarks,
                attempted_ooxml: attempt.attemptedOoxml,
            };
        case "untracked_write_detected":
            return {
                ...common,
                stage: "untracked_write_detected",
                bookmarks: attempt.bookmarks,
                ooxml: encodeOoxmlForWire(attempt.ooxml),
                lost_paragraph_count: attempt.lostParagraphCount,
                added_paragraph_count: attempt.addedParagraphCount,
            };
        case "merge_failed":
            return {
                ...common,
                stage: "merge_failed",
                bookmarks: attempt.bookmarks,
                ooxml: encodeOoxmlForWire(attempt.ooxml),
                word_error: attempt.wordError,
                merge_step: attempt.mergeStep,
            };
        case "backend_repair_exhausted":
            return {
                ...common,
                stage: "backend_repair_exhausted",
                bookmarks: attempt.bookmarks,
                ooxml: encodeOoxmlForWire(attempt.ooxml),
                word_error: attempt.wordError,
                repair_attempts: attempt.repairAttempts,
                repair_failure_message: attempt.repairFailureMessage,
            };
        case "agent_recovery_threw":
            return {
                ...common,
                stage: "agent_recovery_threw",
                bookmarks: attempt.bookmarks,
                ooxml: encodeOoxmlForWire(attempt.ooxml),
                word_error: attempt.wordError,
                agent_failure_message: attempt.agentFailureMessage,
            };
        case "agent_recovery_drifted":
            return {
                ...common,
                stage: "agent_recovery_drifted",
                bookmarks: attempt.bookmarks,
                ooxml: encodeOoxmlForWire(attempt.ooxml),
                word_error: attempt.wordError,
                drift: encodeDriftForWire(attempt.drift),
            };
        case "execute_js_failed":
            return {
                ...common,
                stage: "execute_js_failed",
                script: attempt.script,
                stdout: attempt.stdout,
                script_error: attempt.scriptError,
            };
    }
}

export function encodeApplyOutcomeBatchForWire(
    batch: RedlineApplyOutcomeBatch,
    applyBatchId: string,
): RedlineApplyOutcomeBatchWire {
    return {
        apply_batch_id: applyBatchId,
        apply_initiated_from: batch.applyInitiatedFrom,
        review_id: batch.reviewId,
        agent_role: batch.agentRole,
        entries: batch.entries,
        drops: batch.drops,
        snapshot_ids: batch.snapshotIds,
    };
}
